import { computeDeterministicPositionState, type PrimitiveExecution } from './deterministicPrimitives';

export type TradePathClassification =
  | 'UNAVAILABLE'
  | 'WINNER_TO_LOSER'
  | 'PROFIT_SURRENDERED_TO_FLAT'
  | 'RECOVERED_FROM_HEAT_TO_WIN'
  | 'CLOSED_NEAR_PEAK'
  | 'PROFITABLE_WITH_GIVEBACK'
  | 'LOSER_WITH_RECOVERY'
  | 'CLOSED_NEAR_WORST'
  | 'FLAT_WITHOUT_OPPORTUNITY';

export type ExitQualityClassification =
  | 'EXCELLENT'
  | 'ABOVE_AVERAGE'
  | 'AVERAGE'
  | 'BELOW_AVERAGE'
  | 'UNDERPERFORMING'
  | 'NOT_RATED';

export type NarrativeTone = 'default' | 'profit' | 'loss' | 'neutral';

export type NarrativeSegment = {
  text: string;
  tone?: NarrativeTone;
  emphasis?: boolean;
};

export type InsightDiagnostic = {
  id: 'time_in_trade' | 'peak_to_exit' | 'entries_exits';
  label: string;
  value: string;
  tooltip: string;
  accessibleLabel: string;
  tone: 'positive' | 'negative' | 'neutral' | 'muted';
  precision?: 'seconds' | 'minutes' | 'approximate';
  source: 'broker_execution' | 'market_bars' | 'grouped_executions';
};

export type TradeInsightBadgeModel = {
  label: 'EXIT QUALITY';
  value: ExitQualityClassification;
  text: string;
  tone: 'success' | 'teal' | 'neutral' | 'warning' | 'danger' | 'muted';
  tooltip: string;
  percentile: number | null;
  sampleSize: number;
};

export type TradeInsightExecutionStructure = {
  firstEntryTimestampMs: number | null;
  finalExitTimestampMs: number | null;
  entryCount: number;
  exitCount: number;
  countKind: 'actions' | 'fills';
  positionCycleCount: number;
  selectedPositionCycleId: string | null;
};

export type TradeInsightExecution = PrimitiveExecution & {
  actionGroupId?: string | number | null;
};

export type TradeInsightInput = {
  tradeId?: string | number | null;
  closedPnl?: number | null;
  mae?: number | null;
  mfe?: number | null;
  isOpen?: boolean;
  mfeTimestampMs?: number | null;
  maeTimestampMs?: number | null;
  firstEntryTimestampMs?: number | null;
  finalExitTimestampMs?: number | null;
  executionTimestampResolutionMs?: number | null;
  marketBarResolutionMs?: number | null;
  metricSource?: 'market_bars' | null;
  entryCount?: number | null;
  exitCount?: number | null;
  executionCountKind?: 'actions' | 'fills' | null;
  initialRiskAmount?: number | null;
  peakPositionSize?: number | null;
  instrumentTickSize?: number | null;
  instrumentPointValue?: number | null;
  estimatedRoundTripTradingCosts?: number | null;
  comparableExitRetentions?: Array<number | null | undefined>;
};

export type TradeReviewNote = {
  label: string;
  value: string;
};

export type TradeReviewModel = {
  title: 'TRADE REVIEW';
  items: TradeReviewNote[];
};

export type TradeInsightAnalysis = {
  headerMetadata: string;
  classification: TradePathClassification;
  narrative: {
    classification: TradePathClassification;
    segments: NarrativeSegment[];
    accessibleText: string;
  };
  review: TradeReviewModel;
  diagnostics: InsightDiagnostic[];
  badge: TradeInsightBadgeModel | null;
  calculations: {
    favorablePeak: number | null;
    maximumDrawdown: number | null;
    peakToCloseReversal: number | null;
    recoveryFromWorst: number | null;
    exitRetention: number | null;
    timeInTradeMs: number | null;
    peakToExitDelayMs: number | null;
    meaningfulExcursionFloor: number | null;
    breakevenTolerance: number;
  };
  dataQuality: string[];
};

const MINUS = '\u2212';
const SECOND_MS = 1_000;
const MINUTE_MS = 60_000;
const FIVE_MINUTES_MS = 5 * MINUTE_MS;

const finiteOrNull = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

export const formatTradeInsightMoney = (value: number, options: { signed?: boolean } = {}): string => {
  const norm = Object.is(value, -0) || Math.abs(value) < 0.004 ? 0 : value;
  const abs = Math.abs(norm);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  if (!options.signed) return formatted;
  if (norm > 0) return `+${formatted}`;
  if (norm < 0) return `${MINUS}${formatted}`;
  return formatted;
};

export const formatTradeInsightPercent = (value: number): string => `${Math.round(value)}%`;

export const formatTradeInsightDuration = (
  milliseconds: number | null,
  options: { resolutionMs?: number | null; approximate?: boolean } = {},
): string | null => {
  if (milliseconds == null || !Number.isFinite(milliseconds) || milliseconds < 0) return null;
  const resolutionMs = finiteOrNull(options.resolutionMs) ?? SECOND_MS;
  const approximate = Boolean(options.approximate);

  if (resolutionMs >= MINUTE_MS) {
    if (milliseconds < resolutionMs) return resolutionMs === MINUTE_MS ? '<1m' : 'Same bar';
    const roundedResolution = approximate ? resolutionMs : MINUTE_MS;
    const totalMinutes = Math.max(1, Math.round(milliseconds / roundedResolution) * Math.round(roundedResolution / MINUTE_MS));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${hours > 0 ? String(minutes).padStart(2, '0') : minutes}m`);
    return `${approximate ? '~' : ''}${parts.join(' ')}`;
  }

  const totalSeconds = Math.round(milliseconds / SECOND_MS);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0) parts.push(`${hours > 0 ? String(minutes).padStart(2, '0') : minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${parts.length > 0 ? String(seconds).padStart(2, '0') : seconds}s`);
  return parts.join(' ');
};

export const inferTradeInsightTimestampResolutionMs = (value: unknown): number | null => {
  if (value instanceof Date) return 1;
  if (typeof value === 'number' && Number.isFinite(value)) return value > 1_000_000_000_000 ? 1 : SECOND_MS;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const text = value.trim();
  if (/^\d+(?:\.\d+)?$/.test(text)) return Number(text) > 1_000_000_000_000 ? 1 : SECOND_MS;
  if (/\d{1,2}:\d{2}:\d{2}(?:\.\d+)?/.test(text)) return text.includes('.') ? 1 : SECOND_MS;
  if (/\d{1,2}:\d{2}(?!:)/.test(text)) return MINUTE_MS;
  return null;
};

export const median = (values: Array<number | null | undefined>): number | null => {
  const sorted = values.map(finiteOrNull).filter((value): value is number => value != null).sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

export const midrankPercentile = (value: number, sample: Array<number | null | undefined>): number | null => {
  const values = sample.map(finiteOrNull).filter((item): item is number => item != null);
  if (values.length === 0) return null;
  const countBelow = values.filter((item) => item < value).length;
  const countEqual = values.filter((item) => item === value).length;
  return 100 * (countBelow + 0.5 * countEqual) / values.length;
};

export function getExecutionStructure(executions: TradeInsightExecution[]): TradeInsightExecutionStructure | null {
  const normalizedExecutions = (executions ?? []).filter((execution) => (
    Number.isFinite(Number(execution.timestamp))
    && Number.isFinite(Number(execution.qty))
    && Math.abs(Number(execution.qty)) > 0
    && Number.isFinite(Number(execution.price))
  ));
  if (normalizedExecutions.length === 0) return null;

  const state = computeDeterministicPositionState({ executions: normalizedExecutions, multiplier: 1, markPrice: null });
  const closedPhases = state.phases.filter((phase) => phase.endTime != null);
  const selectedPhase = [...closedPhases].sort((a, b) => (b.endTime ?? 0) - (a.endTime ?? 0))[0] ?? null;
  if (!selectedPhase) return null;

  const sourceById = new Map(normalizedExecutions.map((execution, index) => [String(execution.id ?? `exec-${index}`), execution]));
  const phaseOps = selectedPhase.ops;
  const everyOpHasActionGroup = phaseOps.length > 0 && phaseOps.every((op) => {
    const source = state.applied[op.seq];
    return source != null && sourceById.get(source.sourceId)?.actionGroupId != null;
  });
  const countKind: TradeInsightExecutionStructure['countKind'] = everyOpHasActionGroup ? 'actions' : 'fills';
  const countOps = (kind: 'open' | 'close'): number => {
    const keys = new Set<string>();
    for (const op of phaseOps.filter((candidate) => candidate.kind === kind)) {
      const source = state.applied[op.seq];
      const execution = source ? sourceById.get(source.sourceId) : null;
      const group = everyOpHasActionGroup ? String(execution?.actionGroupId) : String(op.seq);
      keys.add(`${kind}:${group}`);
    }
    return keys.size;
  };

  return {
    firstEntryTimestampMs: selectedPhase.startTime,
    finalExitTimestampMs: selectedPhase.endTime,
    entryCount: countOps('open'),
    exitCount: countOps('close'),
    countKind,
    positionCycleCount: state.phases.length,
    selectedPositionCycleId: selectedPhase.id,
  };
}

export function hasCoherentTradeInsightBars(input: {
  executions: TradeInsightExecution[];
  bars: Array<{ time: number; high: number; low: number }>;
  marketBarResolutionMs: number | null;
}): boolean {
  const resolution = finiteOrNull(input.marketBarResolutionMs);
  const structure = getExecutionStructure(input.executions);
  if (resolution == null || resolution <= 0 || !structure || structure.firstEntryTimestampMs == null || structure.finalExitTimestampMs == null) return false;
  const executions = (input.executions ?? []).filter((execution) => {
    const time = Number(execution.timestamp);
    return Number.isFinite(time) && Number.isFinite(Number(execution.price))
      && time >= structure.firstEntryTimestampMs! && time <= structure.finalExitTimestampMs!;
  });
  if (executions.length === 0) return false;
  const candidateBars = (input.bars ?? []).filter((bar) =>
    Number.isFinite(Number(bar.time)) && Number.isFinite(Number(bar.high)) && Number.isFinite(Number(bar.low)));
  if (candidateBars.length === 0) return false;
  return executions.every((execution) => {
    const executionTime = Number(execution.timestamp);
    const executionPrice = Number(execution.price);
    const nearest = candidateBars.reduce<(typeof candidateBars)[number] | null>((best, bar) =>
      best == null || Math.abs(Number(bar.time) - executionTime) < Math.abs(Number(best.time) - executionTime) ? bar : best, null);
    if (!nearest || Math.abs(Number(nearest.time) - executionTime) > resolution) return false;
    const low = Math.min(Number(nearest.low), Number(nearest.high));
    const high = Math.max(Number(nearest.low), Number(nearest.high));
    const tolerance = Math.max(Math.abs(executionPrice) * 0.05, Number.EPSILON);
    return executionPrice >= low - tolerance && executionPrice <= high + tolerance;
  });
}

export function calculateTimeInTrade(input: {
  firstEntryTimestampMs: number | null;
  finalExitTimestampMs: number | null;
}): number | null {
  const first = finiteOrNull(input.firstEntryTimestampMs);
  const final = finiteOrNull(input.finalExitTimestampMs);
  if (first == null || final == null || final < first) return null;
  return final - first;
}

export function calculatePeakToExit(input: {
  hasMeaningfulMfe: boolean;
  mfeBarTimestampMs: number | null;
  finalExitTimestampMs: number | null;
  marketBarResolutionMs: number | null;
  metricSource?: 'market_bars' | null;
}): { value: number | null; displayValue: string | null; tooltip: string | null; precision?: InsightDiagnostic['precision']; issue?: string } {
  if (!input.hasMeaningfulMfe) return { value: null, displayValue: null, tooltip: null };
  const peak = finiteOrNull(input.mfeBarTimestampMs);
  const exit = finiteOrNull(input.finalExitTimestampMs);
  const resolution = finiteOrNull(input.marketBarResolutionMs);
  if (input.metricSource !== 'market_bars' || peak == null || exit == null || resolution == null) {
    return { value: null, displayValue: null, tooltip: null, issue: 'market-bar peak timing unavailable' };
  }
  if (resolution <= 0 || resolution > FIVE_MINUTES_MS) {
    return { value: null, displayValue: null, tooltip: null, issue: 'market-bar resolution unsupported for intraday peak timing' };
  }
  if (exit < peak) return { value: null, displayValue: null, tooltip: null, issue: 'MFE bar timestamp occurs after final exit timestamp' };

  const value = exit - peak;
  const sameBar = Math.floor(exit / resolution) === Math.floor(peak / resolution);
  const label = resolution >= MINUTE_MS ? `${Math.round(resolution / MINUTE_MS)}-minute` : 'second-level';
  const tooltip = `Time from peak profit to final exit, estimated from ${label} bars.`;
  if (sameBar) {
    return {
      value,
      displayValue: resolution === MINUTE_MS ? '<1m' : 'Same bar',
      tooltip,
      precision: resolution === FIVE_MINUTES_MS ? 'approximate' : 'minutes',
    };
  }
  return {
    value,
    displayValue: formatTradeInsightDuration(value, { resolutionMs: resolution, approximate: resolution === FIVE_MINUTES_MS }),
    tooltip,
    precision: resolution === FIVE_MINUTES_MS ? 'approximate' : resolution >= MINUTE_MS ? 'minutes' : 'seconds',
  };
}

function getMeaningfulExcursionFloor(input: TradeInsightInput): number | null {
  const candidates: number[] = [];
  const tickSize = finiteOrNull(input.instrumentTickSize);
  const pointValue = finiteOrNull(input.instrumentPointValue);
  const peakSize = finiteOrNull(input.peakPositionSize);
  if (tickSize != null && tickSize > 0 && pointValue != null && pointValue > 0 && peakSize != null && peakSize > 0) {
    candidates.push(2 * tickSize * pointValue * peakSize);
  }
  const costs = finiteOrNull(input.estimatedRoundTripTradingCosts);
  if (costs != null && costs > 0) candidates.push(3 * Math.abs(costs));
  const initialRisk = finiteOrNull(input.initialRiskAmount);
  if (initialRisk != null && initialRisk > 0) candidates.push(0.10 * initialRisk);
  return candidates.length > 0 ? Math.max(...candidates) : null;
}

function getBreakevenTolerance(input: TradeInsightInput): number {
  const tickSize = finiteOrNull(input.instrumentTickSize);
  const pointValue = finiteOrNull(input.instrumentPointValue);
  const peakSize = finiteOrNull(input.peakPositionSize);
  const oneTick = tickSize != null && tickSize > 0 && pointValue != null && pointValue > 0 && peakSize != null && peakSize > 0
    ? tickSize * pointValue * peakSize
    : null;
  const costs = finiteOrNull(input.estimatedRoundTripTradingCosts);
  return Math.max(0, oneTick ?? 0, costs != null ? Math.abs(costs) : 0);
}


function segment(text: string, tone: NarrativeTone = 'default', emphasis = false): NarrativeSegment {
  return { text, tone, emphasis };
}

function narrative(classification: TradePathClassification, segments: NarrativeSegment[]): TradeInsightAnalysis['narrative'] {
  return { classification, segments, accessibleText: segments.map((item) => item.text).join('') };
}

export function buildTradeInsightHeaderMetadata(input: TradeInsightInput): string {
  const entryCount = finiteOrNull(input.entryCount) ?? 1;
  const exitCount = finiteOrNull(input.exitCount) ?? 1;
  const countKind = input.executionCountKind ?? 'actions';

  const entryText = countKind === 'fills'
    ? `${entryCount} Entry Fill${entryCount === 1 ? '' : 's'}`
    : `${entryCount} ${entryCount === 1 ? 'Entry' : 'Entries'}`;

  const exitText = countKind === 'fills'
    ? `${exitCount} Exit Fill${exitCount === 1 ? '' : 's'}`
    : `${exitCount} ${exitCount === 1 ? 'Exit' : 'Exits'}`;

  const timeInTradeMs = calculateTimeInTrade({
    firstEntryTimestampMs: finiteOrNull(input.firstEntryTimestampMs),
    finalExitTimestampMs: finiteOrNull(input.finalExitTimestampMs),
  });
  const timeText = formatTradeInsightDuration(timeInTradeMs, { resolutionMs: input.executionTimestampResolutionMs });

  const parts = [entryText, exitText];
  if (timeText) parts.push(timeText);
  return parts.join(' · ');
}

export function buildTradeInsightNarrative(input: TradeInsightInput): TradeInsightAnalysis['narrative'] {
  const closedPnl = finiteOrNull(input.closedPnl);

  if (closedPnl == null || input.isOpen) {
    const timeInTradeMs = calculateTimeInTrade({
      firstEntryTimestampMs: finiteOrNull(input.firstEntryTimestampMs),
      finalExitTimestampMs: finiteOrNull(input.finalExitTimestampMs),
    });
    const timeText = formatTradeInsightDuration(timeInTradeMs, { resolutionMs: input.executionTimestampResolutionMs });
    const message = timeText
      ? `This trade was held for ${timeText} and is currently open. Detailed excursion data will be available once closed.`
      : 'This trade is currently open. Detailed excursion data will be available once closed.';
    return narrative('UNAVAILABLE', [segment(message)]);
  }

  const normClosedPnl = Object.is(closedPnl, -0) || Math.abs(closedPnl) < 0.004 ? 0 : closedPnl;
  const pnlTone: NarrativeTone = normClosedPnl > 0 ? 'profit' : normClosedPnl < 0 ? 'loss' : 'neutral';
  const closedPnlText = formatTradeInsightMoney(normClosedPnl, { signed: true });

  const timeInTradeMs = calculateTimeInTrade({
    firstEntryTimestampMs: finiteOrNull(input.firstEntryTimestampMs),
    finalExitTimestampMs: finiteOrNull(input.finalExitTimestampMs),
  });
  const timeText = formatTradeInsightDuration(timeInTradeMs, { resolutionMs: input.executionTimestampResolutionMs });

  const sentence1Segments: NarrativeSegment[] = timeText
    ? [
        segment('This trade closed at '),
        segment(closedPnlText, pnlTone, true),
        segment(' after '),
        segment(timeText, 'default', true),
        segment('.'),
      ]
    : [
        segment('This trade closed at '),
        segment(closedPnlText, pnlTone, true),
        segment('.'),
      ];

  const rawMfe = finiteOrNull(input.mfe);
  const rawMae = finiteOrNull(input.mae);

  let sentence2Segments: NarrativeSegment[] | null = null;
  let classification: TradePathClassification = 'UNAVAILABLE';

  if (rawMfe != null && !Number.isNaN(rawMfe)) {
    const mfe = Object.is(rawMfe, -0) || Math.abs(rawMfe) < 0.004 ? 0 : rawMfe;
    const mae = rawMae != null && !Number.isNaN(rawMae) ? (rawMae <= 0 ? rawMae : -rawMae) : null;
    const normMae = mae != null ? (Object.is(mae, -0) || Math.abs(mae) < 0.004 ? 0 : mae) : null;

    if (normClosedPnl > 0) {
      if (mfe >= normClosedPnl) {
        const giveback = round2(mfe - normClosedPnl);
        const normGiveback = Object.is(giveback, -0) || Math.abs(giveback) < 0.004 ? 0 : giveback;
        if (normGiveback > 0) {
          classification = 'PROFITABLE_WITH_GIVEBACK';
          sentence2Segments = [
            segment('It reached '),
            segment(formatTradeInsightMoney(mfe, { signed: true }), 'profit', true),
            segment(' at its favorable peak, then gave back '),
            segment(formatTradeInsightMoney(normGiveback), 'default', true),
            segment(' before the final exit.'),
          ];
        } else {
          classification = 'CLOSED_NEAR_PEAK';
          sentence2Segments = [
            segment('It closed at or near its favorable peak.'),
          ];
        }
      } else {
        classification = 'UNAVAILABLE';
      }
    } else if (normClosedPnl < 0) {
      if (mfe > 0) {
        const reversal = round2(mfe - normClosedPnl);
        const normReversal = Object.is(reversal, -0) || Math.abs(reversal) < 0.004 ? 0 : reversal;
        classification = 'WINNER_TO_LOSER';
        sentence2Segments = [
          segment('It reached '),
          segment(formatTradeInsightMoney(mfe, { signed: true }), 'profit', true),
          segment(' at its favorable peak, then reversed '),
          segment(formatTradeInsightMoney(normReversal), 'default', true),
          segment(' before the final exit.'),
        ];
      } else if (mfe <= 0) {
        if (normMae != null && normMae <= normClosedPnl) {
          const recovery = round2(normClosedPnl - normMae);
          const normRecovery = Object.is(recovery, -0) || Math.abs(recovery) < 0.004 ? 0 : recovery;
          classification = 'LOSER_WITH_RECOVERY';
          sentence2Segments = [
            segment('It never moved into profit and reached a max drawdown of '),
            segment(formatTradeInsightMoney(normMae, { signed: true }), 'loss', true),
            segment(' before recovering '),
            segment(formatTradeInsightMoney(normRecovery), 'profit', true),
            segment(' into the final exit.'),
          ];
        } else {
          classification = 'UNAVAILABLE';
        }
      }
    } else {
      // normClosedPnl === 0
      if (mfe > 0) {
        const giveback = round2(mfe);
        classification = 'PROFIT_SURRENDERED_TO_FLAT';
        sentence2Segments = [
          segment('It reached '),
          segment(formatTradeInsightMoney(mfe, { signed: true }), 'profit', true),
          segment(' at its favorable peak, then gave back '),
          segment(formatTradeInsightMoney(giveback), 'default', true),
          segment(' before the final exit.'),
        ];
      } else if (mfe <= 0 && normMae != null && normMae <= 0) {
        const recovery = round2(-normMae);
        classification = 'FLAT_WITHOUT_OPPORTUNITY';
        sentence2Segments = [
          segment('It never moved into profit and reached a max drawdown of '),
          segment(formatTradeInsightMoney(normMae, { signed: true }), 'loss', true),
          segment(' before recovering '),
          segment(formatTradeInsightMoney(recovery), 'profit', true),
          segment(' into the final exit.'),
        ];
      }
    }
  }

  if (!sentence2Segments) {
    sentence2Segments = [
      segment('Detailed excursion data is unavailable for this trade.'),
    ];
  }

  const combinedSegments = [
    ...sentence1Segments,
    segment(' '),
    ...sentence2Segments,
  ];

  return narrative(classification, combinedSegments);
}

export function buildExecutionFallbackNarrative(input: TradeInsightInput): TradeInsightAnalysis['narrative'] {
  return buildTradeInsightNarrative(input);
}

export function buildTradeInsightDiagnostics(input: {
  timeInTradeMs: number | null;
  executionTimestampResolutionMs?: number | null;
  peakToExit: ReturnType<typeof calculatePeakToExit>;
  entryCount?: number | null;
  exitCount?: number | null;
  countKind?: 'actions' | 'fills' | null;
}): InsightDiagnostic[] {
  const diagnostics: InsightDiagnostic[] = [];
  const timeText = formatTradeInsightDuration(input.timeInTradeMs, { resolutionMs: input.executionTimestampResolutionMs });
  if (timeText) diagnostics.push({ id: 'time_in_trade', label: 'TIME IN TRADE', value: timeText, tooltip: 'Time from first entry to final exit.', accessibleLabel: `Time in trade ${timeText}.`, tone: 'neutral', precision: (input.executionTimestampResolutionMs ?? SECOND_MS) >= MINUTE_MS ? 'minutes' : 'seconds', source: 'broker_execution' });
  if (input.peakToExit.displayValue && input.peakToExit.tooltip) diagnostics.push({ id: 'peak_to_exit', label: 'PEAK-TO-EXIT', value: input.peakToExit.displayValue, tooltip: input.peakToExit.tooltip, accessibleLabel: `Peak-to-exit ${input.peakToExit.displayValue}.`, tone: 'neutral', precision: input.peakToExit.precision, source: 'market_bars' });
  if (diagnostics.length < 2) {
    const entryCount = finiteOrNull(input.entryCount);
    const exitCount = finiteOrNull(input.exitCount);
    if (entryCount != null && entryCount > 0 && exitCount != null && exitCount > 0) {
      const fills = input.countKind === 'fills';
      const value = fills ? `${entryCount} · ${exitCount}` : `${entryCount} entr${entryCount === 1 ? 'y' : 'ies'} · ${exitCount} exit${exitCount === 1 ? '' : 's'}`;
      diagnostics.push({ id: 'entries_exits', label: fills ? 'ENTRY / EXIT FILLS' : 'ENTRIES / EXITS', value, tooltip: fills ? 'Number of verified entry and exit fills in this trade.' : 'Number of entry and exit actions in this trade.', accessibleLabel: fills ? `${entryCount} entry fill${entryCount === 1 ? '' : 's'} and ${exitCount} exit fill${exitCount === 1 ? '' : 's'}.` : `${entryCount} ${entryCount === 1 ? 'entry' : 'entries'} and ${exitCount} exit${exitCount === 1 ? '' : 's'}.`, tone: 'neutral', source: 'grouped_executions' });
    }
  }
  return diagnostics.slice(0, 2);
}

export function buildTradeReview(input: TradeInsightInput): TradeReviewModel {
  const closedPnl = finiteOrNull(input.closedPnl);
  const rawMfe = finiteOrNull(input.mfe);
  const rawMae = finiteOrNull(input.mae);

  if (closedPnl == null || input.isOpen || rawMfe == null || Number.isNaN(rawMfe)) {
    return {
      title: 'TRADE REVIEW',
      items: [
        { label: 'For Review', value: 'Use executions and notes to review this trade.' },
      ],
    };
  }

  const normClosedPnl = Object.is(closedPnl, -0) || Math.abs(closedPnl) < 0.004 ? 0 : closedPnl;
  const mfe = Object.is(rawMfe, -0) || Math.abs(rawMfe) < 0.004 ? 0 : rawMfe;
  const mae = rawMae != null && !Number.isNaN(rawMae) ? (rawMae <= 0 ? rawMae : -rawMae) : null;
  const normMae = mae != null ? (Object.is(mae, -0) || Math.abs(mae) < 0.004 ? 0 : mae) : null;

  if (normClosedPnl > 0) {
    if (mfe < normClosedPnl) {
      return {
        title: 'TRADE REVIEW',
        items: [
          { label: 'For Review', value: 'Use executions and notes to review this trade.' },
        ],
      };
    }
    const exitRetention = mfe > 0 ? normClosedPnl / mfe : 1;
    if (exitRetention >= 0.75) {
      return {
        title: 'TRADE REVIEW',
        items: [
          { label: 'Positives', value: 'Profit was mostly protected.' },
          { label: 'For Review', value: 'Check whether the exit matched your plan.' },
        ],
      };
    } else {
      return {
        title: 'TRADE REVIEW',
        items: [
          { label: 'Primary Issue', value: 'Some profit was given back.' },
          { label: 'For Review', value: 'Review your scale-out or trailing exit plan.' },
        ],
      };
    }
  } else if (normClosedPnl < 0) {
    if (mfe > 0) {
      return {
        title: 'TRADE REVIEW',
        items: [
          { label: 'Primary Issue', value: 'Favorable move fully reversed.' },
          { label: 'For Review', value: 'Review the exit plan after profit appears.' },
        ],
      };
    } else if (mfe <= 0) {
      if (normMae != null && normMae < normClosedPnl) {
        return {
          title: 'TRADE REVIEW',
          items: [
            { label: 'Positives', value: 'Loss was reduced before exit.' },
            { label: 'For Review', value: 'Review entry timing and risk cut.' },
          ],
        };
      } else if (normMae != null && normMae <= normClosedPnl) {
        return {
          title: 'TRADE REVIEW',
          items: [
            { label: 'Primary Issue', value: 'Trade never moved in your favor.' },
            { label: 'For Review', value: 'Review entry timing and confirmation.' },
          ],
        };
      } else {
        return {
          title: 'TRADE REVIEW',
          items: [
            { label: 'For Review', value: 'Use executions and notes to review this trade.' },
          ],
        };
      }
    }
  }

  // normClosedPnl === 0
  if (mfe > 0) {
    return {
      title: 'TRADE REVIEW',
      items: [
        { label: 'Primary Issue', value: 'Some profit was given back.' },
        { label: 'For Review', value: 'Review your scale-out or trailing exit plan.' },
      ],
    };
  }

  return {
    title: 'TRADE REVIEW',
    items: [
      { label: 'For Review', value: 'Use executions and notes to review this trade.' },
    ],
  };
}

export function buildTradeInsightAnalysis(input: TradeInsightInput): TradeInsightAnalysis {
  const closedPnl = finiteOrNull(input.closedPnl);
  const mfe = finiteOrNull(input.mfe);
  const mae = finiteOrNull(input.mae);
  const mfeTimestampMs = finiteOrNull(input.mfeTimestampMs);
  const firstEntryTimestampMs = finiteOrNull(input.firstEntryTimestampMs);
  const finalExitTimestampMs = finiteOrNull(input.finalExitTimestampMs);
  const meaningfulFloor = getMeaningfulExcursionFloor(input);
  const breakevenTolerance = getBreakevenTolerance(input);
  const favorablePeak = mfe != null && mfe > 0 ? mfe : null;
  const maximumDrawdown = mae != null ? (mae <= 0 ? mae : -mae) : null;
  const peakToCloseReversal = favorablePeak != null && closedPnl != null ? round2(favorablePeak - closedPnl) : null;
  const recoveryFromWorst = closedPnl != null && maximumDrawdown != null ? round2(closedPnl - maximumDrawdown) : null;
  const exitRetention = favorablePeak != null && favorablePeak > 0 && closedPnl != null ? closedPnl / favorablePeak : null;
  const hasMeaningfulMfe = favorablePeak != null && meaningfulFloor != null && favorablePeak > meaningfulFloor;
  const timeInTradeMs = calculateTimeInTrade({ firstEntryTimestampMs, finalExitTimestampMs });
  const peakToExit = calculatePeakToExit({ hasMeaningfulMfe, mfeBarTimestampMs: mfeTimestampMs, finalExitTimestampMs, marketBarResolutionMs: finiteOrNull(input.marketBarResolutionMs), metricSource: input.metricSource });
  const dataQuality: string[] = [];
  if (meaningfulFloor == null) dataQuality.push('meaningful excursion floor unavailable');
  if (firstEntryTimestampMs != null && finalExitTimestampMs != null && timeInTradeMs == null) dataQuality.push('final exit timestamp occurs before first entry timestamp');
  if (peakToExit.issue) dataQuality.push(peakToExit.issue);

  const headerMetadata = buildTradeInsightHeaderMetadata(input);
  const narrativeResult = buildTradeInsightNarrative(input);
  const review = buildTradeReview(input);
  const classification = narrativeResult.classification;

  return {
    headerMetadata,
    classification,
    narrative: narrativeResult,
    review,
    diagnostics: [],
    badge: null,
    calculations: { favorablePeak, maximumDrawdown, peakToCloseReversal, recoveryFromWorst, exitRetention, timeInTradeMs, peakToExitDelayMs: peakToExit.value, meaningfulExcursionFloor: meaningfulFloor, breakevenTolerance },
    dataQuality,
  };
}
